import { CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FileAddOutlined } from "@ant-design/icons";
import { ViewScriptResultBtn } from "@src/frontend/components/ViewScriptResultBtn";
import { useUserClaims } from "@src/frontend/hooks/useUserClaims";
import type { UserScriptAction } from "@src/frontend/utils/types";
import type { UserScriptModel } from "@src/generated/prisma/models";
import type { ScriptActionPayload } from "@src/utils/agent";
import { Badge, Button, Card, Flex, Popconfirm, Tag, Typography } from "antd";
import { useCallback, useMemo } from "react";

interface Props {
  script: UserScriptModel;
  onClick?: (e: UserScriptModel) => void;
  onDelete?: (e: UserScriptModel) => Promise<void>;
  onQuickAddClick?: (title: string, keys: string[]) => void;
  onCopy?: (e: UserScriptModel) => void;
}

export const ActionItem = (props: Props) => {
  const [claims] = useUserClaims();
  const { script: e, onDelete, onClick, onQuickAddClick } = props;
  const actions = useMemo<UserScriptAction[] | undefined>(() => e.actions as unknown as UserScriptAction[], [e]);
  const resource = useMemo(() => {
    const keys = actions?.filter(e => (e.payload as ScriptActionPayload<'INPUT'>).type === 'res_key')
      .map(e => (e.payload as ScriptActionPayload<'INPUT'>).value);
    return new Set(keys ?? []).values().toArray();
  }, [actions]);
  const handleQuickAddClick = useCallback(() => {
    onQuickAddClick?.(e.name, resource);
  }, [resource, e, onQuickAddClick]);
  return (
    <>
      <Badge.Ribbon key={e.id} color={e.public ? 'red' : 'blue'} text={e.public ? '公开' : '私有'}>
        <Card className="surface-card" title={e.name}
          actions={claims?.sub === e.logto_uid ? [
            <Popconfirm
              title={'删除'}
              description="确定要删除吗？"
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => onDelete?.(e)}
            >
              <Button danger size="small" type="link"><DeleteOutlined /></Button>
            </Popconfirm>,
            <Button size="small" type="link" onClick={() => onClick?.(e)}><EditOutlined /></Button>,
            <ViewScriptResultBtn
              script_id={e.id}
              btnProps={{ size: 'small', type: 'link' }}
            />,
            <Button size="small" type="link" onClick={() => props.onCopy?.(e)}><CopyOutlined /></Button>,
          ] : [
            <Button size="small" type="link" onClick={() => props.onCopy?.(e)}><CopyOutlined /></Button>,
            <Button size="small" type="link" onClick={() => onClick?.(e)}><EyeOutlined /></Button>,
            <ViewScriptResultBtn
              script_id={e.id}
              btnProps={{ size: 'small', type: 'link' }}
            />,
          ]}
        >
          <Flex vertical gap={8}>
            <Typography.Text style={{
              whiteSpace: 'pre-line',
            }} type="secondary">{e.description || '暂无介绍'}</Typography.Text>
            {resource.length > 0 && (
              <>
                <Flex vertical gap={8}>
                  <Flex gap={4}>
                    {resource.map(v => <Tag key={v}>{v}</Tag>)}
                  </Flex>
                  <Button onClick={handleQuickAddClick} icon={<FileAddOutlined />}>创建资源变量</Button>
                </Flex>
              </>
            )}
            <Typography.Text type="secondary">共 {actions?.length} 个步骤</Typography.Text>
          </Flex>
        </Card>
      </Badge.Ribbon>
    </>
  );
}
